import React, { useEffect, useRef } from "react";
import { Container } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import * as PIXI from "pixi.js";
import anime from "animejs";
import LoaderController from "./LoaderController";
import { setLoaderComplete } from "../../../../State/Slices/loadingProgressSlice";

// Black background for loader
const LOADER_BACKGROUND_COLOR = 0x000000;


const Loader = () => {
  const { dimensions, device, orientation } = useDimensions();
  const loadingProgress = useSelector((state) => state.loadingProgress);
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const overlayGraphicsRef = useRef(null);
  const loaderControllerRef = useRef(null);
  const fadeOutStartedRef = useRef(false);
  const errorShownRef = useRef(false);

  // Create black overlay background
  useEffect(() => {
    if (containerRef.current && !overlayGraphicsRef.current) {
      const graphics = new PIXI.Graphics();
      graphics.beginFill(LOADER_BACKGROUND_COLOR);
      graphics.drawRect(0, 0, dimensions.width, dimensions.height);
      graphics.endFill();
      graphics.zIndex = 0;

      containerRef.current.addChild(graphics);
      overlayGraphicsRef.current = graphics;
      containerRef.current.alpha = 1;
    }

    return () => {
      if (overlayGraphicsRef.current) {
        overlayGraphicsRef.current.destroy();
        overlayGraphicsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update overlay dimensions when screen size changes
  useEffect(() => {
    if (overlayGraphicsRef.current) {
      overlayGraphicsRef.current.clear();
      overlayGraphicsRef.current.beginFill(LOADER_BACKGROUND_COLOR);
      overlayGraphicsRef.current.drawRect(
        0,
        0,
        dimensions.width,
        dimensions.height,
      );
      overlayGraphicsRef.current.endFill();
    }
  }, [dimensions.width, dimensions.height]);

  // Initialize spine controller on mount
  useEffect(() => {
    if (containerRef.current && !loaderControllerRef.current && dimensions) {
      loaderControllerRef.current = new LoaderController(
        containerRef.current,
        dimensions,
        { device, orientation },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update spine when dimensions change
  useEffect(() => {
    if (loaderControllerRef.current) {
      loaderControllerRef.current.updateDimensions(dimensions, { device, orientation });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, device, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loaderControllerRef.current) {
        loaderControllerRef.current.cleanup();
        loaderControllerRef.current = null;
      }
    };
  }, []);

  // Handle loading error — show error fallback UI
  useEffect(() => {
    if (
      loadingProgress.status === "error" &&
      loaderControllerRef.current &&
      !errorShownRef.current
    ) {
      errorShownRef.current = true;
      loaderControllerRef.current.showError();
    }
  }, [loadingProgress.status]);

  // When loading completes → play "out" → hold frozen → mount game → settle → fade out
  useEffect(() => {
    if (
      loadingProgress.status === "success" &&
      containerRef.current &&
      !fadeOutStartedRef.current
    ) {
      fadeOutStartedRef.current = true;

      const controller = loaderControllerRef.current;
      if (!controller) {
        if (containerRef.current) containerRef.current.visible = false;
        dispatch(setLoaderComplete());
        return;
      }

      // Step 1: Play "out" animation, loader stays visible after it finishes
      controller.onLoadingComplete().then(() => {
        if (!containerRef.current) return;

        // Step 2: "out" is done — loader frozen on last frame. Mount the game.
        dispatch(setLoaderComplete());

        // Step 3: Wait for game to settle (count stable frames after mount)
        const SETTLE_FRAMES = 10;
        let stableCount = 0;

        const waitForSettle = () => {
          stableCount++;
          if (stableCount < SETTLE_FRAMES) {
            requestAnimationFrame(waitForSettle);
            return;
          }

          // Step 4: Game has settled — fade out the loader
          if (!containerRef.current) return;
          anime({
            targets: containerRef.current,
            alpha: [1, 0],
            duration: 300,
            easing: "easeOutQuad",
            complete: () => {
              if (containerRef.current) {
                containerRef.current.visible = false;
              }
              if (loaderControllerRef.current) {
                loaderControllerRef.current.cleanup();
                loaderControllerRef.current = null;
              }
            },
          });
        };

        requestAnimationFrame(waitForSettle);
      });
    }
  }, [loadingProgress.status, dispatch]);

  return (
    <Container
      name="loader"
      ref={containerRef}
      x={0}
      y={0}
      zIndex={10001}
      sortableChildren
    />
  );
};

export default Loader;
